import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';

import { Slider } from '@atlas/components';
import { CasinoApi, SiteConfigManager, useCasinoNewState, useSettings } from '@atlas/core';
import { CASINO_TILE_TYPES, CasinoGameTopTile, Label } from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import { CDN_TYPE_CMS, imageUrlCDN } from 'common/utils/utils.js';
import { useIsRtl } from 'hooks/useIsRtl';
import { getGameIdentifier } from 'src/helpers/casino';

import InfoGame from '../GameList/GameTile';

const getImageUrl = item => {
  const customImageUrl = item.imageUrl ? encodeURI(imageUrlCDN(item.imageUrl, CDN_TYPE_CMS)) : null;
  return !item.useDefaultIcon && customImageUrl ? customImageUrl : null;
};

const LobbyGameTopCategoriesBlock = ({ blockTitleKey, blocks, imageUrl }) => {
  const [isShowImg, setIsShowImg] = useState(true);
  const handleError = useCallback(() => setIsShowImg(false), []);

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);
  const casinoGameTileType = SiteConfigManager.getConfig(
    'config.config.general.mobileCasinoGameTileView',
    CASINO_TILE_TYPES.TYPE_5,
  );
  const tileTypeClass = {
    'casino-game-top-tile-type_1': casinoGameTileType === CASINO_TILE_TYPES.TYPE_1,
    'casino-game-top-tile-type_2': casinoGameTileType === CASINO_TILE_TYPES.TYPE_2,
    'casino-game-top-tile-type_3': casinoGameTileType === CASINO_TILE_TYPES.TYPE_3,
    'casino-game-top-tile-type_4': casinoGameTileType === CASINO_TILE_TYPES.TYPE_4,
    'casino-game-top-tile-type_5': casinoGameTileType === CASINO_TILE_TYPES.TYPE_5,
    'casino-game-top-tile-type_6': casinoGameTileType === CASINO_TILE_TYPES.TYPE_6,
  };

  const { group } = useCasinoNewState();
  const { lang } = useSettings();

  const [fetchedGames, setFetchedGames] = useState(null);
  const [gameOverlayId, setGameOverlayId] = useState(null);

  const gameIds = useMemo(() => {
    return (blocks || []).reduce((acc, item) => {
      const id = Number(item.gameId ?? item.id);
      if (id && !Number.isNaN(id)) {
        acc.push(id);
      }
      return acc;
    }, []);
  }, [blocks]);

  useEffect(() => {
    if (gameIds.length === 0) {
      setFetchedGames([]);
      return;
    }

    CasinoApi.fetchCasinoGames({ group, ids: gameIds, lang })
      .then(response => {
        setFetchedGames(response?.games || []);
      })
      .catch(err => {
        console.error('Failed to fetch active games status', err);
        setFetchedGames([]);
      });
  }, [gameIds, lang, group]);

  const finalItems = useMemo(() => {
    const list = blocks || [];
    if (fetchedGames === null) {
      return list.map(item => {
        const gId = item.gameId || item.id;
        return {
          finalImageUrl: getImageUrl(item),
          gId,
          tileGame: {
            id: Number(gId),
            name: item.gameName || '',
            providerDisplayedName: item.providerName || '',
            providerName: item.providerName || '',
          },
        };
      });
    }

    const activeGamesMap = new Map(fetchedGames.map(game => [String(game.id), game]));

    return list
      .filter(item => activeGamesMap.has(String(item.gameId || item.id)))
      .map(item => {
        const gId = item.gameId || item.id;
        const gameData = activeGamesMap.get(String(gId));
        return {
          finalImageUrl: getImageUrl(item),
          gId,
          tileGame: {
            ...gameData,
            providerName: gameData.providerName || gameData.providerDisplayedName,
          },
        };
      });
  }, [blocks, fetchedGames]);

  const toggleShowOverlay = useCallback(game => {
    const gameIdentifier = getGameIdentifier(game);
    setGameOverlayId(prevGameOverlayId =>
      prevGameOverlayId !== gameIdentifier ? gameIdentifier : null,
    );
  }, []);

  if (!finalItems || finalItems.length === 0) {
    return null;
  }

  const isPending = fetchedGames === null;

  return (
    <div className="casino-lobby__item lobby-game-top">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={24}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/top-games.png`
                }
                width={24}
              />
            </div>
          )}
          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
      </div>
      <div className="casino-lobby__content">
        <div className="lobby-game-top__slider">
          <Slider
            autoplay={false}
            breakpointsBase="container"
            dir={dir}
            direction="horizontal"
            loop={false}
            slidesPerView={'auto'}
            spaceBetween={16}
          >
            {finalItems.map(({ finalImageUrl, gId, tileGame }, index) => {
              const gameIdentifier = getGameIdentifier(tileGame);
              const showOverlay = gameOverlayId === gameIdentifier;

              return (
                <Slider.Slide key={`${gId}-${index}`}>
                  <CasinoGameTopTile
                    className={cx(index >= 9 && 'casino-game-top-tile-large', tileTypeClass)}
                    index={index}
                    isMobile={true}
                  >
                    <InfoGame
                      game={tileGame}
                      imageOverride={finalImageUrl}
                      isLoading={isPending}
                      showOverlay={showOverlay}
                      size="S"
                      toggleShowOverlay={toggleShowOverlay}
                    />
                  </CasinoGameTopTile>
                </Slider.Slide>
              );
            })}
          </Slider>
        </div>
      </div>
    </div>
  );
};

LobbyGameTopCategoriesBlock.propTypes = {
  blockTitleKey: PropTypes.string,
  blocks: PropTypes.array,
  imageUrl: PropTypes.string,
};

export default LobbyGameTopCategoriesBlock;
